// Report analysis workflow: which files each application's analysis reads, and the page's state.
//
// Sections, modes and roles mirror lud-backend utils/reportValidation/validationRegistry.js. The
// backend re-checks everything; this only drives the UI.
// App keys and labels come from the documentation registry (APPS); there is no second app list.

import { APPS } from "../ludDocuments/utils/ludDocuments.constants";
import JahezLogo from "../../assets/dp-logo/jahez.svg";
import MarsoolLogo from "../../assets/dp-logo/marsool.svg";
import HungerLogo from "../../assets/dp-logo/hungerstation.svg";
import KeetaLogo from "../../assets/dp-logo/keeta.svg";
import ToyouLogo from "../../assets/dp-logo/toyou.svg";
import NinjaLogo from "../../assets/dp-logo/ninja.svg";

const ORDERS = { ar: "الطلبات", en: "Orders" };
const FEES = { ar: "التعويضات", en: "Fees" };
const MONTHLY = { ar: "التقرير الشهري", en: "Monthly report" };

// section key -> { role, modes, multi, label }
const SECTIONS = {
  jahez: {
    Orderstatus: { role: "orders", modes: ["W", "M"], multi: true, label: ORDERS },
    fees: { role: "fees", modes: ["W", "M"], multi: false, label: FEES },
    monthly: { role: "monthly", modes: ["M"], multi: false, label: MONTHLY },
  },
  marsool: {
    marsoolOrder: { role: "orders", modes: ["W"], multi: true, label: ORDERS },
    marsoolFees: { role: "fees", modes: ["W"], multi: false, label: FEES },
    marsoolMonthly: { role: "monthly", modes: ["M"], multi: false, label: MONTHLY },
  },
  hungerstation: {
    HungerOrders: { role: "orders", modes: ["W"], multi: false, label: ORDERS },
    HungerMonthly: { role: "monthly", modes: ["M"], multi: true, label: MONTHLY },
  },
  keeta: {
    keetaOrders: { role: "orders", modes: ["W"], multi: false, label: ORDERS },
    keetaMonthly: { role: "monthly", modes: ["M"], multi: true, label: MONTHLY },
    keetaOrderMonthly: { role: "orders", modes: ["M"], multi: false, label: ORDERS },
  },
  toyou: {
    toyouOrders: { role: "orders", modes: ["W"], multi: false, label: ORDERS },
    toyouFees: { role: "fees", modes: ["W"], multi: false, label: FEES },
    toyouMonthly: { role: "monthly", modes: ["M"], multi: false, label: MONTHLY },
  },
  ninja: {
    ninjsOrders: { role: "orders", modes: ["W"], multi: false, label: ORDERS },
    ninjaFees: { role: "fees", modes: ["W", "M"], multi: false, label: FEES },
    ninjaMonthly: { role: "monthly", modes: ["M"], multi: true, label: MONTHLY },
  },
};

const KIND_BY_MODE_SECTION_ROLE = {
  W: { orders: "daily_orders", fees: "daily_compensations" },
  M: { orders: "monthly_orders", fees: "monthly_compensations", monthly: "monthly_report" },
};

export const APP_KEYS = ["hungerstation", "jahez", "marsool", "ninja", "keeta", "toyou"];
export const APP_LOGOS = { jahez: JahezLogo, marsool: MarsoolLogo, hungerstation: HungerLogo, keeta: KeetaLogo, toyou: ToyouLogo, ninja: NinjaLogo };
export const appLabel = (app, lang) => APPS[app]?.label?.[lang] || app;
export const ACCEPTED_FILES = ".xlsx,.xls,.csv";
const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];

export const sectionsFor = (app, mode) => Object.entries(SECTIONS[app] || {})
  .filter(([, section]) => section.modes.includes(mode))
  .map(([key, section]) => ({ key, ...section, kind: KIND_BY_MODE_SECTION_ROLE[mode][section.role] }));
export const kindOf = (app, mode, sectionKey) => sectionsFor(app, mode).find((section) => section.key === sectionKey)?.kind || null;
export const sectionLabel = (app, sectionKey, lang) => SECTIONS[app]?.[sectionKey]?.label?.[lang] || sectionKey;

// Existing report-file parts the app's analysis reads for this month (all of them must be sent).
export function partsFor(parts, { app, mode, year, month }) {
  const sections = sectionsFor(app, mode);
  return parts
    .filter((part) => part.app === app && Number(part.year) === Number(year) && Number(part.month) === Number(month))
    .map((part) => {
      if (!part.partKey) return { ...part, legacy: true, section: null };
      const section = sections.find((item) => item.kind === part.kind);
      return section ? { ...part, section: section.key } : null;
    })
    .filter(Boolean)
    .filter((part) => !part.legacy || sections.some((section) => legacyMatches(part, section, mode)));
}
// Files from before versioning are shown (they block the analysis) when they are of a section's kind.
const LEGACY_TYPE_PERIOD = {
  daily_orders: ["daily", "orders"], daily_compensations: ["daily", "compensations"], monthly_orders: ["monthly", "orders"],
  monthly_compensations: ["monthly", "compensations"], monthly_report: ["monthly", "monthlyReport"],
};
const legacyMatches = (part, section) => {
  const [type, period] = LEGACY_TYPE_PERIOD[section.kind] || [];
  return part.type === type && part.period === period;
};

export const fileProblem = (file, limits = {}) => {
  const name = String(file?.name || "").toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))) return "UNSUPPORTED_FILE_TYPE";
  if (limits.maxFileBytes && file.size > limits.maxFileBytes) return "FILE_TOO_LARGE";
  if (!file.size) return "EMPTY_FILE";
  return null;
};

// ---- state ----------------------------------------------------------------------------------------

// The five steps of /auth/lud: Store & period -> Applications & files -> Checks -> Preview -> Save & result.
export const STEPS = ["setup", "files", "validate", "preview", "result"];

export const initialState = {
  step: "setup",
  context: { storeCode: "", storeId: "", mode: "W", year: "", month: "" },
  apps: [],
  uploads: [], // [{ id, app, section, file, name, size, operation: "add" | "replace", partKey?, expectedVersion?, problem? }]
  parts: [], // the store's report-file parts (server)
  partsLoaded: false,
  validation: null,
  confirmationToken: null,
  acknowledgements: [], // warning codes the user explicitly acknowledged (SOURCE_PERIOD_DIFFERS_FROM_SELECTED)
  preview: null,
  result: null,
  error: null, // { code, status, phase, step, details }
  phase: "idle", // idle | preparing | uploading | validating | previewing | committing
  precheck: false, // Checks shows problems found before anything was sent (submission().blockers)
  progress: null,
};

export const setupComplete = ({ context }) => Boolean(context.storeCode && context.year && context.month && context.mode);

// Anything that changes what would be analyzed drops the earlier checks: a confirmation or a
// preview never carries over to other files, another store or another month.
const invalidated = (state) => ({ ...state, validation: null, confirmationToken: null, acknowledgements: [], preview: null, result: null, error: null, precheck: false });

let nextId = 1;

export function reducer(state, action) {
  switch (action.type) {
    case "context": {
      const context = { ...state.context, ...action.value };
      const storeChanged = context.storeCode !== state.context.storeCode;
      const scopeChanged = context.mode !== state.context.mode || String(context.year) !== String(state.context.year) || String(context.month) !== String(state.context.month);
      // Files stay when only the month or mode changes, except replacements (they target a saved
      // part of the earlier month) and files of sections the new mode does not read.
      const uploads = storeChanged ? [] : scopeChanged
        ? state.uploads.filter((upload) => upload.operation === "add" && sectionsFor(upload.app, context.mode).some((section) => section.key === upload.section))
        : state.uploads;
      return {
        ...invalidated(state),
        context,
        uploads,
        parts: storeChanged ? [] : state.parts,
        partsLoaded: storeChanged ? false : state.partsLoaded,
      };
    }
    case "goto":
      return { ...state, step: action.step };
    case "toggleApp": {
      const apps = state.apps.includes(action.app) ? state.apps.filter((app) => app !== action.app) : [...state.apps, action.app];
      return { ...invalidated(state), apps, uploads: state.uploads.filter((upload) => apps.includes(upload.app)) };
    }
    case "parts":
      return { ...state, parts: action.parts, partsLoaded: true };
    case "addFiles": {
      const added = action.files.map((file) => ({
        id: `u${nextId++}`, app: action.app, section: action.section, file, name: file.name, size: file.size,
        operation: action.replace ? "replace" : "add",
        ...(action.replace ? { partKey: action.replace.partKey, expectedVersion: action.replace.fileVersion } : {}),
        problem: fileProblem(file, action.limits),
      }));
      // one replacement per part: a new choice replaces the earlier one
      const kept = action.replace ? state.uploads.filter((upload) => upload.partKey !== action.replace.partKey) : state.uploads;
      return { ...invalidated(state), uploads: [...kept, ...added] };
    }
    case "removeUpload":
      return { ...invalidated(state), uploads: state.uploads.filter((upload) => upload.id !== action.id) };
    case "busy":
      return { ...state, phase: action.phase, progress: action.progress ?? null, error: null, precheck: false, ...(action.step ? { step: action.step } : {}) };
    case "precheck":
      // Nothing is sent: Checks lists the problems found on the files.
      return { ...state, step: "validate", precheck: true, validation: null, confirmationToken: null, acknowledgements: [], preview: null, error: null };
    case "cancelled":
      // A check or preview the user left: its answer is ignored, nothing else changes.
      return { ...state, phase: "idle", progress: null, error: null, step: action.step || state.step };
    case "progress":
      return { ...state, progress: action.progress };
    case "validated":
      return { ...state, step: "validate", phase: "idle", progress: null, validation: action.validation, confirmationToken: null, acknowledgements: [], preview: null, precheck: false };
    case "confirmed":
      return { ...state, confirmationToken: action.token, acknowledgements: action.acknowledgements || [] };
    case "previewed":
      return { ...state, step: "preview", phase: "idle", progress: null, preview: action.preview };
    case "committed":
      // Saved: the uploaded files, checks, confirmation and preview are dropped; the result stays.
      return { ...state, step: "result", phase: "idle", progress: null, result: action.result, uploads: [], validation: null, confirmationToken: null, acknowledgements: [], preview: null, error: null };
    case "error":
      return { ...state, phase: "idle", progress: null, error: action.error, ...(action.error?.step ? { step: action.error.step } : {}), ...(action.reset || {}) };
    case "restart":
      return { ...initialState, context: state.context, parts: [], partsLoaded: false };
    default:
      return state;
  }
}

// What the current state would send: per selected app, every existing part (replaced by the user's
// new file, or kept and resent unchanged) and every new part.
// -> { files: [{ ...upload | keep }], blockers: [{ app, code, ... }] }
export function submission(state) {
  const { mode, year, month } = state.context;
  const files = [];
  const blockers = [];
  for (const app of state.apps) {
    const existing = partsFor(state.parts, { app, mode, year, month });
    for (const part of existing) {
      if (part.legacy) {
        blockers.push({ app, code: "LEGACY_PART", part });
        continue;
      }
      const replacement = state.uploads.find((upload) => upload.operation === "replace" && upload.partKey === part.partKey);
      if (replacement) files.push({ ...replacement, kind: part.kind });
      else files.push({ keep: true, app, section: part.section, kind: part.kind, operation: "replace", partKey: part.partKey, expectedVersion: part.fileVersion, part, name: part.currentFile?.uploadedName || part.currentFile?.originalName || part.partLabel || "file" });
    }
    for (const upload of state.uploads.filter((item) => item.app === app && item.operation === "add")) {
      files.push({ ...upload, kind: kindOf(app, mode, upload.section) });
    }
    if (!files.some((file) => file.app === app)) blockers.push({ app, code: "NO_FILES" });
    // several current parts of one report kind may be cumulative weekly snapshots of the same report
    const counts = existing.filter((part) => !part.legacy).reduce((acc, part) => ({ ...acc, [part.kind]: (acc[part.kind] || 0) + 1 }), {});
    for (const [kind, count] of Object.entries(counts)) if (count > 1) blockers.push({ app, code: "SEVERAL_PARTS", kind, count, warningOnly: true });
  }
  for (const upload of state.uploads) if (upload.problem) blockers.push({ app: upload.app, code: upload.problem, name: upload.name });
  return { files, blockers };
}
